import React, { useEffect, useState } from 'react';
import axios from 'axios';

const DealTable = () => {
  // Initialize dealsData as an empty array, it will be populated from the API
  const [dealsData, setDealsData] = useState([]);
  const [activeFilter, setActiveFilter] = useState('Ongoing');
  const [editRow, setEditRow] = useState(null); // Stores the ID of the row being edited
  const [addModalOpen, setAddModalOpen] = useState(false); // Controls 'Add Deal' modal visibility
  const [formData, setFormData] = useState({}); // Stores form data for both edit and add
  const [totalDealsCount, setTotalDealsCount] = useState(0); // New state for total deals count

  // Fetch deals when the component mounts
  useEffect(() => {
    fetchDeals();
  }, []);

  // Function to fetch deals from the backend
  const fetchDeals = async () => {
    try {
      const response = await axios.get('https://admin.thepridehotel.in/api/deals');
      // Format data from backend to frontend's expected structure
      const formattedData = response.data.map(item => ({
        id: `#${item.id}`, // Add '#' prefix for display
        dealName: item.deal_name,
        reservationsLeft: item.reserv_left,
        endDate: item.end_date,
        roomType: item.room_type,
        status: item.status,
      }));
      setDealsData(formattedData);
      setTotalDealsCount(formattedData.length); // Set total deals count
    } catch (error) {
      console.error('Error fetching deals:', error);
      // Optional: Show a user-friendly message
      // alert('Failed to load deals. Please try again later.');
    }
  };

  // Handler for editing a row
  const handleEditClick = (item) => {
    // Remove '#' from ID before setting to editRow as backend expects numeric ID
    setEditRow(item.id.replace('#', ''));
    // Populate formData with existing item data for editing
    setFormData({
      dealName: item.dealName,
      reservationsLeft: item.reservationsLeft,
      endDate: item.endDate,
      roomType: item.roomType,
      status: item.status,
    });
  };

  // Handler for opening the 'Add Deal' modal
  const handleAddClick = () => {
    // Initialize formData with empty values for a new entry
    setFormData({
      dealName: '',
      reservationsLeft: '',
      endDate: '',
      roomType: '',
      status: 'New', // Default status for new deals
    });
    setAddModalOpen(true); // Open the add modal
  };

  // Handler for input changes in the form (both edit and add)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handler for updating an existing deal
  const handleUpdate = async () => {
    try {
      await axios.put(`https://admin.thepridehotel.in/api/deals/${editRow}`, {
        // Send data formatted for backend (matching column names)
        deal_name: formData.dealName, // Match backend column name
        reservations_left: parseInt(formData.reservationsLeft), // Ensure numeric type
        end_date: formData.endDate, // Match backend column name
        room_type: formData.roomType, // Match backend column name
        status: formData.status,
      });
      setEditRow(null); // Close the edit mode
      fetchDeals(); // Re-fetch data to update the table
    } catch (error) {
      console.error('Error updating deal:', error);
      alert('Failed to update deal. Please check console for details.');
    }
  };

  // Handler for adding a new deal
  const handleAdd = async () => {
    console.log('Frontend formData before validation:', formData); // Log formData before validation

    // Client-side validation for required fields
    const { dealName, reservationsLeft, endDate, roomType, status } = formData;

    if (!dealName || dealName.trim() === '') {
      alert('Deal Name is required.');
      return;
    }
    const parsedReservationsLeft = parseInt(reservationsLeft);
    if (isNaN(parsedReservationsLeft) || parsedReservationsLeft < 0) {
      alert('Reservations Left must be a non-negative number.');
      return;
    }
    if (!endDate || endDate.trim() === '') { // Added validation for endDate
      alert('End Date is required.');
      return;
    }
    if (!roomType || roomType.trim() === '') {
      alert('Room Type is required.');
      return;
    }
    if (!status || status.trim() === '') {
      alert('Status is required.');
      return;
    }

    try {
      // Corrected payload to match backend's expected camelCase property names
      const payload = {
        dealName: dealName,
        reservationsLeft: parsedReservationsLeft,
        endDate: endDate, // Use validated endDate
        roomType: roomType,
        status: status,
      };
      console.log('Frontend sending payload:', payload); // Log payload being sent

      await axios.post(`https://admin.thepridehotel.in/api/deals`, payload);
      setAddModalOpen(false); // Close the add modal
      fetchDeals(); // Re-fetch data to update the table
    } catch (error) {
      console.error('Error adding deal:', error);
      if (error.response && error.response.data && error.response.data.error) {
        alert(`Error: ${error.response.data.error}`);
      } else {
        alert('Failed to add deal. Please check console for details.');
      }
    }
  };

  // Handler for closing the modals
  const handleModalClose = () => {
    setEditRow(null);
    setAddModalOpen(false);
  };

  // Internal Styles for DealTable
  const styles = {
    container: {
      fontFamily: "'Open Sans', sans-serif",
      backgroundColor: '#fff',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
      padding: '30px',
      width: '100%',
      maxWidth: '16200px',
      boxSizing: 'border-box',
      margin: '0px auto', // Center the container
      
    },
    headerTitle: {
      fontSize: '24px', // Increased font size for better visibility
      color: '#111827', // Darker color for emphasis
      fontWeight: '600',
      margin: '0',
      marginBottom: '20px',
    },
    topControlsRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      marginBottom: '20px',
      flexWrap: 'wrap', // Allow wrapping on smaller screens
      gap: '10px', // Add gap for spacing
    },
    filterButtonsGroup: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap',
    },
    filterBtn: {
      padding: '7px 16px',
      fontSize: '14px',
      color: '#4B5563',
      background: 'transparent',
      border: '1px solid #D1D5DB',
      borderRadius: '20px',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
      whiteSpace: 'nowrap',
    },
    filterBtnActive: {
      backgroundColor: '#E0F2FE',
      color: '#3B82F6',
      fontWeight: '600',
      borderColor: '#3B82F6',
    },
    actionButtonsGroup: {
        display: 'flex',
        gap: '10px',
        marginLeft: 'auto',
        flexWrap: 'wrap', // Allow wrapping on smaller screens
    },
    addDealBtn: {
      backgroundColor: '#AE7D54',
      color: 'white',
      padding: '10px 20px', // Slightly larger padding
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'background-color 0.2s ease',
    },
    addDealBtnHover: {
        backgroundColor: '#9A6B46',
    },
    filterIconBtn: {
        padding: '8px 14px',
        border: '1px solid #D1D5DB',
        borderRadius: '8px',
        backgroundColor: '#F9FAFB',
        color: '#4B5563',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        fontSize: '14px',
        transition: 'background-color 0.2s ease, color 0.2s ease',
    },
    filterIconBtnHover: {
        backgroundColor: '#E5E7EB',
        color: '#374151',
    },
    tableContainer: {
      overflowX: 'auto',
      border: '1px solid #E5E7EB',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '900px',
      fontSize: '14px',
    },
    tableHeader: {
      textAlign: 'left',
      padding: '16px 20px',
      color: '#6B7280',
      fontWeight: '600',
      borderBottom: '1px solid #E5E7EB',
      backgroundColor: '#F9FAFB',
    },
    tableCell: {
      padding: '16px 20px',
      color: '#111827',
      borderBottom: '1px solid #F3F4F6',
      whiteSpace: 'nowrap',
    },
    tableRowHover: {
      backgroundColor: '#F9FAFB',
    },
    status: {
      padding: '5px 12px',
      borderRadius: '999px',
      fontWeight: '500',
      fontSize: '13px',
      display: 'inline-block',
      textTransform: 'capitalize',
    },
    statusOngoing: {
      background: '#E0F2FE',
      color: '#3B82F6',
    },
    statusFull: {
      background: '#FFE4E6',
      color: '#EF4444',
    },
    statusInactive: {
      background: '#F3F4F6',
      color: '#6B7280',
    },
    statusNew: {
      background: '#DCFCE7',
      color: '#22C55E',
    },
    ellipsisIcon: {
      color: '#9CA3AF',
      cursor: 'pointer',
      transition: 'color 0.2s ease',
    },
    ellipsisIconHover: {
      color: '#4B5563',
    },
    // Modal Styles (copied from RatesTable, ensure consistency)
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    },
    modalContent: {
      background: '#fff',
      padding: '30px',
      borderRadius: '12px',
      width: '90%',
      maxWidth: '450px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      position: 'relative',
    },
    modalCloseButton: {
      position: 'absolute',
      top: '15px',
      right: '15px',
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#9CA3AF',
      lineHeight: 1,
    },
    modalLabel: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    modalInput: {
      width: '100%',
      padding: '10px 14px',
      border: '1px solid #D1D5DB',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '&:focus': {
        borderColor: '#AE7D54',
        boxShadow: '0 0 0 2px rgba(174, 125, 84, 0.2)',
      }
    },
    modalSelect: { // Added style for select input
      width: '100%',
      padding: '10px 14px',
      border: '1px solid #D1D5DB',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
      backgroundColor: '#fff',
      cursor: 'pointer',
    },
    modalButtonsContainer: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },
    modalCancelBtn: {
      padding: '10px 20px',
      border: '1px solid #D1D5DB',
      borderRadius: '8px',
      backgroundColor: '#fff',
      color: '#374151',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'background-color 0.2s ease, border-color 0.2s ease',
    },
    modalSaveBtn: {
      padding: '10px 20px',
      backgroundColor: '#AE7D54',
      color: '#fff',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'background-color 0.2s ease',
    }
  };

  const getStatusStyle = (status) => {
    switch (status.toLowerCase()) {
      case 'ongoing': return styles.statusOngoing;
      case 'full': return styles.statusFull;
      case 'inactive': return styles.statusInactive;
      case 'new': return styles.statusNew;
      default: return {};
    }
  };

  // Filter deals based on activeFilter
  const filteredDeals = dealsData.filter(deal => {
    if (activeFilter === 'All') { // New condition for 'All' filter
      return true; // Show all deals
    }
    if (activeFilter === 'Ongoing') {
      return deal.status === 'Ongoing';
    }
    if (activeFilter === 'Finished') {
      // You'll need logic to determine 'Finished' based on endDate or status
      // For now, let's assume 'Full' or custom logic for finished
      return deal.status === 'Full' || deal.status === 'Inactive'; // Assuming 'Inactive' also means finished
    }
    return true; // Fallback, though 'All' should cover it
  });


  return (
    <div style={styles.container}>
      <h1 style={styles.headerTitle}>Deals Management</h1>

      <div style={styles.topControlsRow}>
        <div style={styles.filterButtonsGroup}>
          <button
            style={{
              ...styles.filterBtn,
              ...(activeFilter === 'All' ? styles.filterBtnActive : {}),
            }}
            onClick={() => setActiveFilter('All')}
            onMouseOver={(e) => { if (activeFilter !== 'All') e.currentTarget.style.backgroundColor = styles.filterBtnActive.backgroundColor; }}
            onMouseOut={(e) => { if (activeFilter !== 'All') e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            All Deals ({totalDealsCount})
          </button>
          <button
            style={{
              ...styles.filterBtn,
              ...(activeFilter === 'Ongoing' ? styles.filterBtnActive : {}),
            }}
            onClick={() => setActiveFilter('Ongoing')}
            onMouseOver={(e) => { if (activeFilter !== 'Ongoing') e.currentTarget.style.backgroundColor = styles.filterBtnActive.backgroundColor; }}
            onMouseOut={(e) => { if (activeFilter !== 'Ongoing') e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            Ongoing
          </button>
          <button
            style={{
              ...styles.filterBtn,
              ...(activeFilter === 'Finished' ? styles.filterBtnActive : {}),
            }}
            onClick={() => setActiveFilter('Finished')}
            onMouseOver={(e) => { if (activeFilter !== 'Finished') e.currentTarget.style.backgroundColor = styles.filterBtnActive.backgroundColor; }}
            onMouseOut={(e) => { if (activeFilter !== 'Finished') e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            Finished
          </button>
        </div>

        <div style={styles.actionButtonsGroup}>
            <button
                style={styles.addDealBtn}
                onClick={handleAddClick} // Add onClick handler
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.addDealBtnHover.backgroundColor}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.addDealBtn.backgroundColor}
            >
                Add deal
            </button>
            <button
                style={styles.filterIconBtn}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.filterIconBtnHover.backgroundColor}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.filterIconBtn.backgroundColor}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ marginRight: 5 }}
                >
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                </svg>
                Filter
            </button>
        </div>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>{/* Removed extra whitespace */}
              <th style={styles.tableHeader}>Reference number</th><th style={styles.tableHeader}>Deal name</th><th style={styles.tableHeader}>Reservations left</th><th style={styles.tableHeader}>End date</th><th style={styles.tableHeader}>Room type</th><th style={styles.tableHeader}>Status</th><th style={styles.tableHeader}></th> {/* For ellipsis icon */}
            </tr>
          </thead>
          <tbody>
            {filteredDeals.map((deal, index) => ( // Use filteredDeals here
              <tr
                key={deal.id}
                style={{
                  ...(index === filteredDeals.length - 1 ? { borderBottom: 'none' } : { borderBottom: styles.tableCell.borderBottom }),
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >{/* Removed extra whitespace */}
                <td style={styles.tableCell}>{deal.id}</td><td style={styles.tableCell}>{deal.dealName}</td><td style={styles.tableCell}>{deal.reservationsLeft}</td><td style={styles.tableCell}>{deal.endDate}</td><td style={styles.tableCell}>{deal.roomType}</td><td style={styles.tableCell}>
                  <span style={{ ...styles.status, ...getStatusStyle(deal.status) }}>
                    {deal.status}
                  </span>
                </td><td style={styles.tableCell}>
                  <svg
                    style={styles.ellipsisIcon}
                    onClick={() => handleEditClick(deal)} // Add onClick handler
                    xmlns="http://www.w3.org/2000/svg"
                    width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    onMouseOver={(e) => e.currentTarget.style.color = styles.ellipsisIconHover.color}
                    onMouseOut={(e) => e.currentTarget.style.color = styles.ellipsisIcon.color}
                  >
                    <circle cx="12" cy="12" r="1"></circle>
                    <circle cx="12" cy="5" r="1"></circle>
                    <circle cx="12" cy="19" r="1"></circle>
                  </svg>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal for Add/Edit Deal */}
      {(editRow || addModalOpen) && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <button style={styles.modalCloseButton} onClick={handleModalClose}>&times;</button>
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
              {editRow ? 'Edit Deal' : 'Add New Deal'}
            </h3>
            {[
              { name: 'dealName', label: 'Deal Name' },
              { name: 'reservationsLeft', label: 'Reservations Left', type: 'number' },
              { name: 'endDate', label: 'End Date', type: 'date' }, // Changed type to 'date'
              { name: 'roomType', label: 'Room Type' },
            ].map((field) => (
              <div key={field.name} style={{ marginBottom: '16px' }}>
                <label style={styles.modalLabel}>
                  {field.label}
                </label>
                <input
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleInputChange}
                  type={field.type || 'text'}
                  style={styles.modalInput}
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                  readOnly={editRow && field.name === 'id'} // ID should not be editable
                />
              </div>
            ))}

            {/* Status Dropdown */}
            <div style={{ marginBottom: '16px' }}>
              <label style={styles.modalLabel}>Status</label>
              <select
                name="status"
                value={formData.status || ''}
                onChange={handleInputChange}
                style={styles.modalSelect}
              >
                <option value="">Select Status</option>
                <option value="Ongoing">Ongoing</option>
                <option value="Full">Full</option>
                <option value="Inactive">Inactive</option>
                <option value="New">New</option>
              </select>
            </div>

            <div style={styles.modalButtonsContainer}>
              <button
                onClick={handleModalClose}
                style={styles.modalCancelBtn}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; e.currentTarget.style.borderColor = '#9CA3AF'; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.borderColor = '#D1D5DB'; }}
              >
                Cancel
              </button>
              <button
                onClick={editRow ? handleUpdate : handleAdd}
                style={styles.modalSaveBtn}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#9A6B46'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#AE7D54'}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealTable;
